import { BadRequestException } from '@zaoblako/shared/common/exceptions/bad-request.exception';
import type { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import type { ValidationOptionsInterface } from '@frm-errors/interfaces/validation.options.interface';
import { plainToClass, type ClassConstructor } from 'class-transformer';
import { validate as validateAsync, validateSync, type ValidationError } from 'class-validator';
import { isEmpty, negate, isNil } from 'lodash';

function makeApiError<T>(errors: ValidationError[]): BadRequestException<T> {
  const result = new BadRequestException<T>('Ошибка валидации');
  result.validationErrors = {} as any;

  const mapIteratee = (
    target: ValidationErrorInterface<any>,
    { property, children, constraints, value }: ValidationError,
  ): void => {
    if (isEmpty(children)) {
      target[property] = Object
        .values(constraints)
        .join(', ');
    } else {
      if (!target[property]) {
        target[property] = Array.isArray(value) ? [] as any : {} as any;
      }
      if (Array.isArray(value)) {
        console.log(children);
        value.forEach((item, index) => {
          const child = children.find(err => err.value === item);
          if (child) {
            mapIteratee(target[property] as any, child);
          } else {
            target[property][index] = null;
          }
        });
        console.log(target[property]);
      } else {
        children.forEach(item => mapIteratee(target[property] as any, item));
      }
    }
  };
  errors.forEach(item => mapIteratee(result.validationErrors as ValidationErrorInterface<any>, item));

  return result;
}

/**
 * Validate received value using `class-tranformer` and `class-validator` packages:
 *   - Transform the value into an instance of the class if it is not one;
 *   - Validate transformed value;
 *   - Build and return validation error if validation failed;
 *   - Otherwise, return `null`.
 *
 * @template T Type value to validate
 *
 * @param value Value to validate
 * @param cls Validation value class (decorated by `class-transformer` and `class-validator`)
 * @param [opts] Additional options ({@link ValidationOptionsInterface} type object)
 *
 * @returns API error with validation errors data ({@link BadRequestException} type object)
 * or `null` if validation succeed
 */
export function validate<T>(value: T, cls: ClassConstructor<T>, opts?: ValidationOptionsInterface): BadRequestException<T> {
  const transformed = value instanceof cls ? value : plainToClass(cls, value, opts?.transform);

  const errors = validateSync(<any>transformed, opts?.validation)
  if (isEmpty(errors)) {
    return null;
  }

  return makeApiError(errors);
}

/**
 * Validatereceived value using `class-tranformer` and `class-validator` packages:
 *   - Transform the value into an instance of the class if it is not one;
 *   - Validate transformed value;
 *   - Build and return validation error if validation failed;
 *   - Otherwise, return `null`.
 *
 * Async validations will be used too.
 *
 * @template T Type value to validate
 *
 * @param value Value to validate
 * @param cls Validation value class (decorated by `class-transformer` and `class-validator`)
 * @param [opts] Additional options ({@link ValidationOptionsInterface} type object)
 *
 * @returns API error with validation errors data ({@link BadRequestException} type object)
 * or `null` if validation succeed
 */
validate.async = async function<T>(value: T, cls: ClassConstructor<T>, opts?: ValidationOptionsInterface): Promise<BadRequestException<T>> {
  const transformed = value instanceof cls ? value : plainToClass(cls, value, opts?.transform);

  console.log(opts);

  const errors = await validateAsync(<any>transformed, opts?.validation);
  if (isEmpty(errors)) {
    return null;
  }

  return makeApiError(errors);
};

/**
 * Validate array of values using `class-tranformer` and `class-validator` packages:
 *   - If empty array received, then return `null`;
 *   - Validate each value using {@link validate} function;
 *   - If at least some element is not valid then create and return validation error with mapped array;
 *   - Otherwise, return `null`;
 *
 * @template T Type value to validate
 *
 * @param values Values array to validate
 * @param cls Validation value class (decorated by `class-transformer` and `class-validator`)
 * @param [opts] Additional options ({@link ValidationOptionsInterface} type object)
 *
 * @returns API error with validation errors data ({@link BadRequestException} type object)
 * or `null` if validation succeed
 */
validate.many = function<T>(values: T[], cls: ClassConstructor<T>, opts?: ValidationOptionsInterface): BadRequestException<T[]> {
  if (isEmpty(values)) {
    return null;
  }

  const sets = values.map(value => validate(value, cls, opts));
  if (sets.some(negate(isNil))) {
    const result = new BadRequestException<T[]>('Ошибка валидации');
    result.validationErrors = sets.map(item => item?.validationErrors ?? null) as ValidationErrorInterface<T>[];
    return result;
  } else {
    return null;
  }
};

/**
 * Validate array of values using `class-tranformer` and `class-validator` packages:
 *   - If empty array received, then return `null`;
 *   - Validate each value using {@link validate} function;
 *   - If at least some element is not valid then create and return validation error with mapped array;
 *   - Otherwise, return `null`;
 *
 * Async validations will be used too.
 *
 * @template T Type value to validate
 *
 * @param values Values array to validate
 * @param cls Validation value class (decorated by `class-transformer` and `class-validator`)
 * @param [opts] Additional options ({@link ValidationOptionsInterface} type object)
 *
 * @returns API error with validation errors data ({@link BadRequestException} type object)
 * or `null` if validation succeed
 */
validate.manyAsync = async function<T>(values: T[], cls: ClassConstructor<T>, opts?: ValidationOptionsInterface): Promise<BadRequestException<T[]>> {
  if (isEmpty(values)) {
    return null;
  }

  const sets = await Promise.all(values.map(value => validate.async(value, cls, opts)));
  if (sets.some(negate(isNil))) {
    const result = new BadRequestException<T[]>('Ошибка валидации');
    result.validationErrors = sets.map(item => item.validationErrors) as ValidationErrorInterface<T>[];
    return result;
  } else {
    return null;
  }
};
